// The market's price, live: Hyperliquid's candle closes for the window, then every mark the page
// has seen, drawn on a canvas. On it, each open call's entry and bust, and the band the slip's
// multiplier leaves before either side busts. Numbers here are for drawing only.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Instrument } from "../../sdk";
import { candles, type Interval } from "../../hl/info";
import { fmt, scalePlaces, usd } from "../../lib/num";
import { useAccount } from "../../state/account";
import { usePref, setPref } from "../../state/prefs";
import { useStore } from "../../state/store";
import { markNumber, ticksOf, type Tick } from "../../state/ticks";
import { previews, slip } from "./slip-state";

export const RANGES = [
  { value: "5m", label: "5M", ms: 5 * 60_000, interval: "1m", count: 8 },
  { value: "15m", label: "15M", ms: 15 * 60_000, interval: "1m", count: 18 },
  { value: "1h", label: "1H", ms: 60 * 60_000, interval: "1m", count: 64 },
  { value: "1d", label: "1D", ms: 24 * 60 * 60_000, interval: "15m", count: 98 },
] as const satisfies readonly { value: string; label: string; ms: number; interval: Interval; count: number }[];

type Range = (typeof RANGES)[number];

const INTERVAL_MS: Record<string, number> = { "1m": 60_000, "5m": 300_000, "15m": 900_000 };

type Palette = { up: string; down: string; ink: string; muted: string; faint: string; line: string; accent: string; bg: string; warn: string };

function palette(): Palette {
  const css = getComputedStyle(document.documentElement);
  const read = (name: string) => css.getPropertyValue(name).trim();
  return {
    up: read("--c-up"),
    down: read("--c-down"),
    ink: read("--c-ink"),
    muted: read("--c-muted"),
    faint: read("--c-faint"),
    line: read("--c-line"),
    accent: read("--c-accent"),
    bg: read("--c-surface"),
    warn: read("--c-warn"),
  };
}

type Level = { price: number; color: string; label: string; dash: number[]; width: number; side: "left" | "right" };

type Scene = {
  levels: Level[];
  band: { lo: number; hi: number; aim: "up" | "down" | null } | null;
};

/** `#rrggbb` with an alpha. */
function alpha(hex: string, a: number): string {
  const clean = hex.replace("#", "");
  const value = Number.parseInt(clean.length === 3 ? clean.replace(/./g, "$&$&") : clean, 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${a})`;
}

function hhmmss(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

/** The window the chart shows. */
export function RangePicker() {
  const rangeValue = usePref("range");
  return (
    <div className="lc-ranges" role="radiogroup" aria-label="Chart window">
      {RANGES.map((item) => (
        <button key={item.value} type="button" role="radio" aria-checked={item.value === rangeValue} className="lc-range" onClick={() => setPref("range", item.value)}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

/** The chart, full bleed. `top` keeps the line clear of what is laid over the chart's top; `gutter`
 * lines its tags and live end up with the page's edges. */
export function LiveChart({ instrument, top = 20, gutter = 16 }: { instrument: Instrument; top?: number; gutter?: number }) {
  const host = useRef<HTMLDivElement>(null);
  const inset = useRef({ top, gutter });
  useEffect(() => {
    inset.current = { top, gutter };
  }, [top, gutter]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const rangeValue = usePref("range");
  const range: Range = RANGES.find((item) => item.value === rangeValue) ?? RANGES[1];
  const account = useAccount();
  const preview = useStore(previews);
  const aim = useStore(slip, (state) => state.aim);
  const [empty, setEmpty] = useState(true);
  const places = scalePlaces(instrument.priceScale);
  const coin = instrument.symbol;

  const history = useQuery({
    queryKey: ["chartHistory", coin, range.interval, range.count],
    queryFn: ({ signal }) => candles(coin, range.interval, range.count, signal),
    refetchInterval: INTERVAL_MS[range.interval] ?? 60_000,
    staleTime: 15_000,
  });

  // what the frame loop reads, kept current without restarting it
  const sceneRef = useRef<Scene>({ levels: [], band: null });
  const historyRef = useRef<Tick[]>([]);
  const view = useRef({ lo: 0, hi: 0, ready: false, coin: "", rangeMs: 0 });
  const hover = useRef<{ x: number; y: number } | null>(null);
  const colors = useRef<Palette | null>(null);

  useEffect(() => {
    const step = INTERVAL_MS[range.interval] ?? 60_000;
    const now = Date.now();
    historyRef.current = (history.data ?? [])
      .map((candle) => ({ t: Math.min(candle.t + step, now), p: Number(candle.c) }))
      .filter((tick) => Number.isFinite(tick.p) && tick.p > 0);
  }, [history.data, range.interval]);

  useEffect(() => {
    view.current.ready = false;
  }, [coin, range.value]);

  // the levels to draw: open calls here, and the slip's band
  useEffect(() => {
    const c = colors.current ?? palette();
    colors.current = c;
    const scale = instrument.priceScale;
    const levels: Level[] = [];
    const mine = (account?.positions ?? []).filter((position) => position.instrumentId === instrument.id).slice(0, 6);
    for (const position of mine) {
      const tone = position.isLong ? c.up : c.down;
      const arrow = position.isLong ? "▲" : "▼";
      levels.push({ price: markNumber(position.entryPrice, scale), color: tone, label: `${arrow} ${usd(position.margin, { compact: 5 })}`, dash: [2, 3], width: 1, side: "left" });
      levels.push({ price: markNumber(position.bustPrice, scale), color: c.down, label: `${arrow} bust`, dash: [], width: 1, side: "left" });
    }
    let band: Scene["band"] = null;
    if (preview && preview.instrumentId === instrument.id && preview.up.bust && preview.down.bust) {
      band = { lo: markNumber(preview.up.bust, scale), hi: markNumber(preview.down.bust, scale), aim };
    }
    sceneRef.current = { levels, band };
  }, [account?.positions, preview, aim, instrument.id, instrument.priceScale]);

  // the canvas, sized to its box
  useEffect(() => {
    const box = host.current;
    const el = canvas.current;
    if (!box || !el) return;
    const fit = () => {
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const { width, height } = box.getBoundingClientRect();
      el.width = Math.round(width * ratio);
      el.height = Math.round(height * ratio);
      el.style.width = `${width}px`;
      el.style.height = `${height}px`;
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  // the frame loop: about 30 a second while the tab is visible
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    let frame = 0;
    let last = 0;
    let wasEmpty = true;

    const draw = (time: number) => {
      frame = requestAnimationFrame(draw);
      if (time - last < 32 || document.visibilityState !== "visible") return;
      last = time;
      const c = colors.current ?? palette();
      colors.current = c;
      const ratio = el.width / Math.max(1, el.clientWidth);
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width === 0 || height === 0) return;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const now = Date.now();
      const span = range.ms;
      const { top: padTop, gutter } = inset.current;
      const padRight = gutter;
      // clear of the table's pulse along the bottom
      const padBottom = 72;
      const plotW = width - padRight;
      const plotH = height - padTop - padBottom;
      const start = now - span;
      const end = now + span * 0.04;
      const x = (t: number) => ((t - start) / (end - start)) * plotW;

      // the series: candle closes up to the first live tick, then the ticks
      const ticks = ticksOf(instrument.id);
      const firstLive = ticks[0]?.t ?? Infinity;
      const points: Tick[] = [];
      // each run from the last point before the window, so the line enters from the left edge
      const from = (list: readonly Tick[]) => Math.max(0, list.findLastIndex((tick) => tick.t < start));
      const history = historyRef.current;
      for (let i = from(history); i < history.length; i += 1) if ((history[i] as Tick).t < firstLive) points.push(history[i] as Tick);
      for (let i = from(ticks); i < ticks.length; i += 1) points.push(ticks[i] as Tick);
      const head = points.at(-1);
      const isEmpty = !head;
      if (isEmpty !== wasEmpty) {
        wasEmpty = isEmpty;
        setEmpty(isEmpty);
      }
      if (!head) return;
      points.push({ t: now, p: head.p });

      // the vertical range: the visible prices, and levels near enough to keep in view
      let lo = Infinity;
      let hi = -Infinity;
      for (const point of points) {
        if (point.t < start) continue;
        if (point.p < lo) lo = point.p;
        if (point.p > hi) hi = point.p;
      }
      if (!Number.isFinite(lo)) {
        lo = head.p;
        hi = head.p;
      }
      const floor = head.p * 0.0006;
      if (hi - lo < floor) {
        const mid = (hi + lo) / 2;
        lo = mid - floor / 2;
        hi = mid + floor / 2;
      }
      const reach = Math.max(hi - lo, floor) * 2.5;
      const scene = sceneRef.current;
      const near = (price: number) => Math.abs(price - head.p) <= reach;
      for (const level of scene.levels) {
        if (!near(level.price)) continue;
        lo = Math.min(lo, level.price);
        hi = Math.max(hi, level.price);
      }
      if (scene.band) {
        if (near(scene.band.lo)) lo = Math.min(lo, scene.band.lo);
        if (near(scene.band.hi)) hi = Math.max(hi, scene.band.hi);
      }
      const pad = (hi - lo) * 0.14;
      lo -= pad;
      hi += pad;
      const v = view.current;
      if (!v.ready || v.coin !== coin || v.rangeMs !== span) {
        v.lo = lo;
        v.hi = hi;
        v.ready = true;
        v.coin = coin;
        v.rangeMs = span;
      } else {
        v.lo += (lo - v.lo) * 0.14;
        v.hi += (hi - v.hi) * 0.14;
      }
      const y = (price: number) => padTop + (1 - (price - v.lo) / (v.hi - v.lo)) * plotH;
      const label = (price: number) => fmt(price.toFixed(places), { places });

      ctx.font = "500 11px 'Inter Tight', Inter, system-ui, sans-serif";
      ctx.textBaseline = "middle";

      // the slip's band: where each side would bust
      if (scene.band) {
        const { lo: bandLo, hi: bandHi, aim: aimed } = scene.band;
        const top = Math.max(padTop, y(bandHi));
        const bottom = Math.min(padTop + plotH, y(bandLo));
        const inView = (price: number) => y(price) >= padTop && y(price) <= padTop + plotH;
        // shaded only when an edge is close enough to see; a band past both edges says nothing
        if (bottom > top && (inView(bandLo) || inView(bandHi))) {
          ctx.fillStyle = alpha(c.ink, 0.022);
          ctx.fillRect(0, top, plotW, bottom - top);
        }
        const edge = (price: number, side: "up" | "down") => {
          const tone = side === "up" ? c.up : c.down;
          const strong = aimed === side;
          const faded = aimed !== null && !strong;
          const word = side === "up" ? "▲ Up busts" : "▼ Down busts";
          const py = y(price);
          // out of view, it says nothing: the band only speaks when it is close
          if (py < padTop || py > padTop + plotH) return;
          ctx.save();
          ctx.setLineDash([5, 5]);
          ctx.strokeStyle = alpha(tone, strong ? 0.95 : faded ? 0.25 : 0.55);
          ctx.lineWidth = strong ? 1.5 : 1;
          ctx.beginPath();
          ctx.moveTo(0, Math.round(py) + 0.5);
          ctx.lineTo(plotW, Math.round(py) + 0.5);
          ctx.stroke();
          ctx.restore();
          ctx.textAlign = "left";
          ctx.fillStyle = alpha(tone, faded ? 0.35 : 0.95);
          ctx.fillText(`${word} ${label(price)}`, gutter, py + (side === "up" ? 10 : -10));
        };
        edge(bandLo, "up");
        edge(bandHi, "down");
      }

      // open calls: entries and busts, their tags nudged apart where they would overlap
      const drawn = scene.levels
        .map((level) => ({ level, py: y(level.price) }))
        .filter(({ py }) => py >= padTop - 2 && py <= padTop + plotH + 2)
        .sort((a, b) => a.py - b.py);
      const seen = new Set<string>();
      const tags: { level: Level; y: number }[] = [];
      let lastTag = -Infinity;
      for (const { level, py } of drawn) {
        ctx.save();
        ctx.setLineDash(level.dash);
        ctx.strokeStyle = alpha(level.color, 0.8);
        ctx.lineWidth = level.width;
        ctx.beginPath();
        ctx.moveTo(0, Math.round(py) + 0.5);
        ctx.lineTo(plotW, Math.round(py) + 0.5);
        ctx.stroke();
        ctx.restore();
        const key = `${level.label}:${Math.round(py / 4)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const y = Math.max(py, lastTag + 20);
        lastTag = y;
        tags.push({ level, y });
      }
      // a stack that runs past the plot's foot moves up into it, as far as it has to
      let lowest = padTop + plotH - 9;
      for (let i = tags.length - 1; i >= 0; i -= 1) {
        const tag = tags[i] as (typeof tags)[number];
        tag.y = Math.min(tag.y, lowest);
        lowest = tag.y - 20;
      }
      for (const { level, y: tagY } of tags) {
        const text = level.label;
        const tw = ctx.measureText(text).width + 12;
        // tags on the left, clear of the live price's dot
        const tx = gutter;
        ctx.fillStyle = alpha(c.bg, 0.92);
        ctx.fillRect(tx, tagY - 9, tw, 18);
        ctx.strokeStyle = alpha(level.color, 0.6);
        ctx.setLineDash([]);
        ctx.strokeRect(tx + 0.5, tagY - 8.5, tw - 1, 17);
        ctx.fillStyle = level.color;
        ctx.textAlign = "left";
        ctx.fillText(text, tx + 6, tagY);
      }

      // the line, coloured by the window's direction, over a soft fill
      const first = points.find((point) => point.t >= start) ?? points[0] ?? head;
      const tone = head.p >= first.p ? c.up : c.down;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, plotW, height);
      ctx.clip();
      // through the midpoints between points: the corners of minute closes rounded off
      ctx.beginPath();
      const xy = points.map((point) => [x(point.t), y(point.p)] as const);
      xy.forEach(([px, py], index) => {
        if (index === 0) return ctx.moveTo(px, py);
        const next = xy[index + 1];
        if (!next) return ctx.lineTo(px, py);
        ctx.quadraticCurveTo(px, py, (px + next[0]) / 2, (py + next[1]) / 2);
      });
      const headX = x(now);
      const headY = y(head.p);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = tone;
      ctx.lineWidth = 2;
      ctx.shadowColor = alpha(tone, 0.45);
      ctx.shadowBlur = 12;
      ctx.stroke();
      ctx.shadowBlur = 0;
      // the fill runs to the chart's foot, under the window pills
      ctx.lineTo(headX, height);
      ctx.lineTo(x(points[0]?.t ?? start), height);
      ctx.closePath();
      const fill = ctx.createLinearGradient(0, padTop, 0, height);
      fill.addColorStop(0, alpha(tone, 0.16));
      fill.addColorStop(1, alpha(tone, 0));
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.restore();

      // the head: a dot and a ring that breathes
      const pulse = (now % 1600) / 1600;
      ctx.beginPath();
      ctx.arc(headX, headY, 4 + pulse * 12, 0, Math.PI * 2);
      ctx.fillStyle = alpha(tone, 0.28 * (1 - pulse));
      ctx.fill();
      ctx.beginPath();
      ctx.arc(headX, headY, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = tone;
      ctx.fill();

      // the crosshair
      const pointer = hover.current;
      if (pointer && pointer.x < plotW) {
        const t = start + (pointer.x / plotW) * (end - start);
        let nearest = points[0] ?? head;
        for (const point of points) if (Math.abs(point.t - t) < Math.abs(nearest.t - t)) nearest = point;
        const px = x(nearest.t);
        const py = y(nearest.p);
        ctx.strokeStyle = alpha(c.ink, 0.18);
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(Math.round(px) + 0.5, padTop);
        ctx.lineTo(Math.round(px) + 0.5, padTop + plotH);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fillStyle = c.ink;
        ctx.fill();
        const words = `${hhmmss(nearest.t)}  ${label(nearest.p)}`;
        ctx.font = "500 11px 'Inter Tight', Inter, system-ui, sans-serif";
        const w = ctx.measureText(words).width + 16;
        const bx = Math.min(Math.max(0, px - w / 2), plotW - w);
        // over the point, clear of the line
        const by = py - 30 < padTop ? py + 12 : py - 30;
        ctx.fillStyle = alpha(c.bg, 0.95);
        ctx.fillRect(bx, by, w, 18);
        ctx.fillStyle = c.ink;
        ctx.textAlign = "left";
        ctx.fillText(words, bx + 8, by + 9);
      }
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [instrument.id, coin, range.ms, places]);

  return (
    <div className="lc">
      <div
        className="lc-host"
        ref={host}
        onPointerMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          hover.current = { x: event.clientX - box.left, y: event.clientY - box.top };
        }}
        onPointerLeave={() => {
          hover.current = null;
        }}
      >
        <canvas ref={canvas} className="lc-canvas" aria-label={`${coin} price, last ${range.label}`} role="img" />
        {empty && <div className="lc-wait label">Waiting for the {coin} price</div>}
      </div>
    </div>
  );
}
